/**
 * Brand defaults and env resolving, shared by src/shared/config/brand.js and
 * vite.config.mjs. Plain module: no import.meta, no browser globals.
 * Blank values fall back to defaults. Values containing "[" or "]" are
 * placeholder text: ignored (default used) with a single warning.
 */
export function resolveBrand(env = {}, warn = (message) => console.warn(message)) {
  const warned = new Set();

  const read = (name, fallback) => {
    const value = String(env[name] ?? '').trim();
    if (!value) return fallback;
    if (value.includes('[') || value.includes(']')) {
      if (!warned.has(name)) {
        warned.add(name);
        warn(`${name} must not contain '[' or ']'; using default.`);
      }
      return fallback;
    }
    return value;
  };

  const readUrl = (name, fallback) => {
    const value = read(name, fallback);
    if (/^https?:\/\//.test(value)) return value.replace(/\/+$/, '');
    if (!warned.has(name)) {
      warned.add(name);
      warn(`${name} must start with http:// or https://; using default.`);
    }
    return fallback;
  };

  // Logo override: an http(s) URL or a site path ("/..."). Rejects "//host" and
  // other schemes, since the value ends up in an <img src>.
  const readAsset = (name, fallback) => {
    const value = read(name, fallback);
    if (/^https?:\/\//.test(value) || /^\/(?!\/)/.test(value)) return value;
    if (!warned.has(name)) {
      warned.add(name);
      warn(`${name} must be an http(s) URL or start with "/"; using default.`);
    }
    return fallback;
  };
  const logo = (kind, file) => ({
    light: readAsset(`VITE_BRAND_LOGO_${kind}_URL`, `/brand/${file}-color.svg`),
    dark: readAsset(`VITE_BRAND_LOGO_${kind}_REVERSED_URL`, `/brand/${file}-reversed.svg`),
  });

  const productName = read('VITE_BRAND_PRODUCT_NAME', 'GETRAC');
  const legalEntity = read('VITE_BRAND_LEGAL_ENTITY', 'TEVC Concepts Limited');
  const domain = read('VITE_BRAND_DOMAIN', 'getrac.app');

  const appUrl = readUrl('VITE_BRAND_APP_URL', `https://${domain}`);
  return {
    productName,
    legalEntity,
    companyReg: read('VITE_BRAND_COMPANY_REG', 'RC1108040'),
    ownerLine: read('VITE_BRAND_OWNER_LINE', `${productName}™ is a trademark of ${legalEntity}.`),
    supportEmail: read('VITE_BRAND_SUPPORT_EMAIL', `support@${domain}`),
    privacyEmail: read('VITE_BRAND_PRIVACY_EMAIL', `privacy@${domain}`),
    salesEmail: read('VITE_BRAND_SALES_EMAIL', 'sales@tevcng.com'),
    domain,
    appUrl,
    apiUrl: readUrl('VITE_BRAND_API_URL', `https://api.${domain}`),
    // Live public URL for og:url / og:image; defaults to the app URL
    // (docs/runbooks/domain-cutover.md).
    publicUrl: readUrl('VITE_BRAND_PUBLIC_URL', appUrl),
    // light = logo for a light background (colour file), dark = reversed file.
    logos: {
      horizontal: logo('HORIZONTAL', 'getrac-logo-horizontal'),
      stacked: logo('STACKED', 'getrac-logo-stacked'),
      icon: logo('ICON', 'getrac-icon'),
    },
  };
}
